import * as uuid from 'uuid';

import { type Entity } from '@editor2/types/entity';
import { type Chapter } from '@editor2/types/chapter';
import { type PageGroup } from '@editor2/types/pageGroup';
import { type Page } from '@editor2/types/page';
import {
	type PageElement,
	type DoubleElements,
	ElementType,
	ElementConfigName
} from '@editor2/types/pageElements';
import { isDoubleElement } from '@editor2/services/elementService';
import { beautifyHtml } from '@editor2/services/htmlService';
import { createPopupQuizConfigJson } from './popupQuizService';
import { type Book } from '@editor2/types/book';
import { type LectureSlideDeck } from '@editor2/types/lectureSlideDecks';

export const getFamilyIds = (items) => {
	return items.map((item) => item.family_id);
};

const getChapterPages = (chapter: Chapter, page_groups: Entity<PageGroup>) => {
	return chapter.page_groups
		? [].concat(
				...chapter.page_groups.map((pageGroupId) => page_groups.entities[pageGroupId].pages)
		  )
		: chapter.pages;
};

const addCurrentPageNumber = (
	chapters: Entity<Chapter>,
	page_groups: Entity<PageGroup>,
	pages: Entity<Page>
) => {
	chapters.ids.map((chapterId, chapterIndex) => {
		const chapterPagesIds = getChapterPages(chapters.entities[chapterId], page_groups);

		chapterPagesIds.map((pageId, pageIndex) => {
			const page = pages.entities[pageId];
			const currentPageNumber = page.page_number || `Ch#${chapterIndex + 1} / Pg#${pageIndex + 1}`;

			pages.entities[pageId] = { ...page, currentPageNumber };
		});
	});

	return pages;
};

export const normalizeStore = (items: any[]) => {
	let book: Book;
	const chapters: Entity<Chapter> = { entities: {}, ids: [] };
	const page_groups: Entity<PageGroup> = { entities: {}, ids: [] };
	let pages: Entity<Page> = { entities: {}, ids: [] };
	const elements: Entity<PageElement> = { entities: {}, ids: [] };
	const lecture_slide_decks: Entity<LectureSlideDeck> = { entities: {}, ids: [] };

	items.map((item) => {
		switch (true) {
			case item.type === 'NG::Traditional::Book': {
				book = { ...item };
				break;
			}

			case item.type === 'NG::Soomo::LectureSlideDeck': {
				lecture_slide_decks.entities[item.family_id] = item;
				lecture_slide_decks.ids = [...lecture_slide_decks.ids, item.family_id];
				break;
			}

			case item.type === 'NG::Traditional::Chapter': {
				const { pages, page_groups, ...chapter } = item;

				chapters.entities[item.family_id] = {
					...chapter,
					...(chapter.page_groups_enabled && { page_groups: getFamilyIds(page_groups) }),
					...(!chapter.page_groups_enabled && { pages: getFamilyIds(pages) })
				};

				chapters.ids = [...chapters.ids, item.family_id];
				break;
			}

			case item.type === 'NG::Traditional::PageGroup': {
				const { pages, ...pageGroup } = item;

				page_groups.entities[item.family_id] = {
					...pageGroup,
					pages: getFamilyIds(pages)
				};

				page_groups.ids = [...page_groups.ids, item.family_id];
				break;
			}

			case item.type === 'NG::Traditional::Page': {
				const { elements, ...page } = item;

				pages.entities[item.family_id] = page;
				pages.ids = [...pages.ids, item.family_id];
				break;
			}

			default:
				break;
		}
	});

	pages = addCurrentPageNumber(chapters, page_groups, pages);

	return { book, chapters, page_groups, pages, elements, lecture_slide_decks };
};

const transformElements = (page: Page, elements: Entity<PageElement>) => {
	let indent = false;

	page.elements.map((elementId) => {
		const element = elements.entities[elementId];

		elements.entities[elementId] = {
			...element,
			initPageId: page.family_id,
			indent: isDoubleElement(element.type)
				? indent && (element as DoubleElements).direction === 'in'
				: indent,

			...(element.type === ElementType.HTMLText && {
				body: beautifyHtml(element.body)
			}),

			...(element.type === ElementType.PopupQuiz && {
				config: createPopupQuizConfigJson(element)
			}),
			...((element.type === ElementType.SAQuestion || element.type === ElementType.YourTurn) && {
				exactMatchOverride:
					element.validations.length > 0 &&
					element.validations.some(
						(validation) => validation.name === 'exact-match' && validation.source === 'element'
					),
				validations: element.validations.map((validation) => ({
					...validation,
					clientId: uuid.v4()
				}))
			})
		};

		if (isDoubleElement(element.type)) indent = (element as DoubleElements).direction === 'in';
	});

	return elements;
};

export const normalizePageElements = (items: any[]) => {
	let page: Page;
	let elements: Entity<PageElement> = { entities: {}, ids: [] };

	items.map((item) => {
		switch (true) {
			case item.type === 'NG::Traditional::Page': {
				const { elements, ...pageEntity } = item;
				page = { ...pageEntity, elements: getFamilyIds(elements) };
				break;
			}

			case !!ElementConfigName[item.type]: {
				elements.entities[item.family_id] = item;
				elements.ids = [...elements.ids, item.family_id];
				break;
			}

			default:
				break;
		}
	});

	elements = transformElements(page, elements);

	return { page, elements };
};
